import { Box, Image, useColorMode } from '@chakra-ui/react'
import { Body, Button, Player } from '@missionlabs/react'
import { DownloadIcon } from '@missionlabs/react/circleloop'
import { downloadURL } from '@missionlabs/utils'
import { ImagePlaceholder } from 'features/settings/pages/Integrations/ImagePlaceholder'
import { FC, useRef, useState } from 'react'
import { useWhatsAppDownloadMedia } from 'shared/hooks/useWhatsAppDownloadMedia'

interface SMSMediaDisplayProps {
    mediaData: { ID?: string; url: string; type: string }
}

export const SMSMediaDisplay: FC<SMSMediaDisplayProps> = ({ mediaData }) => {
    const { colorMode } = useColorMode()

    const imgRef = useRef<HTMLImageElement>(null)

    const [downloadedMedia, setDownloadedMedia] = useState<{
        url: string
        type: string
    } | null>(null)

    const handleDownloadMedia = async () => {
        if (!mediaData.url) return

        const media = await downloadWhatsAppMedia(mediaData.url)
        if (media) setDownloadedMedia(media)
    }

    const handleClick = () => {
        if (imgRef.current?.requestFullscreen) {
            imgRef.current.requestFullscreen()
        }
    }

    const {
        downloadWhatsAppMedia,
        loading: downloading,
        error: downloadError
    } = useWhatsAppDownloadMedia()

    if (!mediaData.url) return null

    if (downloadedMedia?.type) {
        const type = downloadedMedia.type.split('/')[0]
        const mediaURL = downloadedMedia.url

        switch (type) {
            case 'image':
                return (
                    <Image
                        ref={imgRef}
                        src={mediaURL}
                        boxSize="128px"
                        fit="contain"
                        onClick={handleClick}
                        cursor="pointer"
                        fallback={<ImagePlaceholder />}
                    />
                )
            case 'video':
                return (
                    <Box as="video" controls width="300" height="200">
                        <source src={mediaURL} type="video/mp4" />
                        Your browser does not support the video tag.
                    </Box>
                )
            case 'audio':
                return (
                    <Player
                        key={mediaURL}
                        sx={{ w: '352px' }}
                        url={mediaURL}
                        onDownload={() => downloadURL(mediaURL)}
                    />
                )
            default:
                break
        }
    }

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadMedia}
                isLoading={downloading}
                disabled={!!downloadedMedia || downloading}
                leftIcon={<DownloadIcon />}
            >
                View Media
            </Button>

            {downloadError && (
                <Body size="sm" color={`${colorMode}.alerts.red`}>
                    {downloadError}
                </Body>
            )}
        </>
    )
}
